import React, { useMemo, useState } from 'react'
import injectSheet from 'react-jss'
import Button from 'pobble-components/dist/button'
import { Modal, ModalContent } from 'pobble-components/dist/modal'
import { useChargebeeScript } from 'pobble-components/dist/subscriptions/utils'
import { apiClient } from 'pobble-components/dist/api'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClock } from '@fortawesome/pro-regular-svg-icons/faClock'
import posthog from 'posthog-js'
import classNames from 'classnames'
import { useHistory, useLocation } from 'react-router-dom'

const getDaysLeft = periodEnd => {
  if (!periodEnd) return null

  const [day, month, year] = periodEnd.split('-').map(Number)
  if (!day || !month || !year) return null

  const end = new Date(year, month - 1, day)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  end.setHours(0, 0, 0, 0)

  return Math.max(0, Math.round((end - today) / (1000 * 60 * 60 * 24)))
}

const getUrgency = daysLeft => {
  if (daysLeft <= 1) return 'critical'
  if (daysLeft >= 3 && daysLeft <= 7) return 'warning'
  return 'default'
}

const getModalCopy = daysLeft => {
  if (daysLeft === 0) {
    return {
      title: 'Trial ends today',
      body:
        "After today, you'll lose access to your daily writing prompt and full lesson library."
    }
  }

  if (daysLeft === 1) {
    return {
      title: 'Trial ends tomorrow',
      body:
        "After today, you'll lose access to your daily writing prompt and full lesson library."
    }
  }

  if (daysLeft >= 2 && daysLeft <= 7) {
    return {
      title: `Only ${daysLeft} days remaining`,
      body:
        "Don't lose your daily writing prompt. Upgrade to keep a fresh lesson idea every day."
    }
  }

  return {
    title: `Your trial ends in ${daysLeft} days`,
    body:
      'Enjoy full access while your trial is active. If Pobble feels like a good fit, you can upgrade anytime to keep everything unlocked.'
  }
}

const getCtaLabel = daysLeft => {
  if (daysLeft === 0) return 'Trial: ends today'
  if (daysLeft === 1) return 'Trial: 1 day left'
  return `Trial: ${daysLeft} days left`
}

export const TrialDaysLeftButton = injectSheet({
  button: {
    marginRight: '16px',
    marginLeft: 'auto',
    background: 'transparent !important',
    color: '#fff !important',
    border: '1px solid #fff !important',
    borderRadius: '16px !important',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.1) !important'
    }
  },
  buttonWarning: {
    background: '#fcefd9 !important',
    color: '#6a3602 !important',
    borderColor: '#6a3602 !important',
    '&:hover': {
      background: '#f8e4c4 !important'
    }
  },
  buttonCritical: {
    background: '#feeaeb !important',
    color: '#601517 !important',
    borderColor: '#601517 !important',
    '&:hover': {
      background: '#fcdadc !important'
    }
  },
  content: {
    maxWidth: '400px',
    textAlign: 'center',
    padding: '32px 28px'
  },
  title: {
    margin: '0 0 12px',
    font: 'var(--heading--)',
    color: 'var(--gray)'
  },
  body: {
    margin: '0 0 24px',
    font: 'var(--body)',
    color: 'var(--gray-)',
    lineHeight: 1.5
  },
  upgradeButton: {
    margin: '0 auto',
    background: 'var(--yellow) !important',
    color: 'var(--gray) !important',
    border: '1px solid var(--gray) !important',
    display: 'flex',
    justifyContent: 'center',
    '&:hover': {
      background: 'var(--yellow-) !important'
    }
  },
  buttonInactive: {
    cursor: 'default !important',
    '&:hover': {
      background: 'transparent !important'
    }
  },
  buttonInactiveWarning: {
    '&:hover': {
      background: '#fcefd9 !important'
    }
  },
  buttonInactiveCritical: {
    '&:hover': {
      background: '#feeaeb !important'
    }
  }
})(function TrialDaysLeftButton({ classes, periodEnd, validPaymentMethod }) {
  const [modalOpen, setModalOpen] = useState(false)
  const { openCheckout } = useChargebeeScript()
  const history = useHistory()
  const location = useLocation()

  const daysLeft = useMemo(() => getDaysLeft(periodEnd), [periodEnd])
  const urgency = getUrgency(daysLeft)
  const modalCopy = getModalCopy(daysLeft)

  if (daysLeft === null) return null

  const isInteractive = validPaymentMethod !== true

  // Kept for easy switch-back to in-app Chargebee checkout
  // eslint-disable-next-line no-unused-vars
  const openUpgradeCheckout = () => {
    posthog.capture('clicked on trial days left upgrade now', {
      days_left: daysLeft
    })
    setModalOpen(false)
    history.replace({
      pathname: location.pathname,
      search: 'from=trial-days-left'
    })
    openCheckout({
      url: 'v3/me/subscriptions/checkout_new_pobble_silver_js',
      params: { annual: true, add_trial: false, discount: false },
      options: {
        step: step => {
          posthog.capture('checkout step', {
            id: 'trial-days-left',
            step
          })
        },
        close: () => {
          history.replace({
            pathname: location.pathname,
            search: false
          })
        }
      }
    })
  }

  const openAddPaymentMethodPortal = () => {
    posthog.capture('clicked on trial days left upgrade now', {
      days_left: daysLeft
    })
    setModalOpen(false)
    apiClient
      .get('/v3/me/subscriptions/continue_after_trial_js')
      .then(response => {
        window.location.href = response.data.hosted_page_object.url
      })
  }

  return (
    <>
      <Button
        icon={<FontAwesomeIcon icon={faClock} />}
        onClick={
          isInteractive
            ? () => {
                posthog.capture('clicked on header trial days left button', {
                  days_left: daysLeft
                })
                setModalOpen(true)
              }
            : undefined
        }
        className={classNames(classes.button, {
          [classes.buttonWarning]: urgency === 'warning',
          [classes.buttonCritical]: urgency === 'critical',
          [classes.buttonInactive]: !isInteractive,
          [classes.buttonInactiveWarning]:
            !isInteractive && urgency === 'warning',
          [classes.buttonInactiveCritical]:
            !isInteractive && urgency === 'critical'
        })}>
        {getCtaLabel(daysLeft)}
      </Button>
      {isInteractive && (
        <Modal
          closeButton
          isOpen={modalOpen}
          requestClose={() => setModalOpen(false)}>
          <ModalContent className={classes.content}>
            <h3 className={classes.title}>{modalCopy.title}</h3>
            <p className={classes.body}>{modalCopy.body}</p>
            <Button tall minw action={openAddPaymentMethodPortal}>
              Upgrade now
            </Button>
          </ModalContent>
        </Modal>
      )}
    </>
  )
})
